import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Download,
  Info,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);

const fmtINR = (v) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(num(v));

const computeDueDate = (fund, wageMonth) => {
  if (!wageMonth) return "";
  const [y, m] = wageMonth.split("-").map(Number);
  if (!y || !m) return "";
  let ny = y, nm = m + 1;
  if (nm > 12) { nm = 1; ny += 1; }
  return `${ny}-${String(nm).padStart(2, "0")}-15`;
};

const computeDelay = (dueDate, paymentDate) => {
  if (!dueDate || !paymentDate) return null;
  const d1 = new Date(dueDate);
  const d2 = new Date(paymentDate);
  if (isNaN(d1) || isNaN(d2)) return null;
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
};

const monthLabel = (wm) => {
  if (!wm) return "";
  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const [y, m] = wm.split("-").map(Number);
  return y && m ? `${months[m - 1]}-${y}` : wm;
};

export default function TaxAuditClause20bPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [records, setRecords] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [parseErrors, setParseErrors] = useState([]);
  const [meta, setMeta] = useState({ client_name: "", pan: "", ay: "2026-27", tan: "" });

  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  const totals = useMemo(() => {
    let emp = 0, allow = 0, disallow = 0;
    for (const r of records) {
      emp += num(r.employees_contribution);
      allow += num(r.allowable_amount);
      disallow += num(r.disallowable_amount);
    }
    return { emp, allow, disallow };
  }, [records]);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const valid = list.filter((f) =>
      /\.(pdf|png|jpe?g|webp)$/i.test(f.name),
    );
    if (valid.length < list.length) {
      toast.warning(`${list.length - valid.length} file(s) skipped — only PDF/PNG/JPG supported.`);
    }
    setFiles((prev) => [...prev, ...valid]);
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!files.length) { toast.error("Upload at least one challan first."); return; }
    setIsParsing(true);
    setParseErrors([]);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));

    // Long timeout — 12 files in parallel via Gemini can take up to ~90 sec
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4 * 60 * 1000); // 4 min

    try {
      toast.info(`Reading ${files.length} challan${files.length > 1 ? "s" : ""} — this may take up to a minute…`);
      const res = await fetch(`${API}/api/tax-audit/clause-20b/parse`, {
        method: "POST",
        body: fd,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${errText.slice(0, 200)}`);
      }
      const data = await res.json();
      const enriched = (data.records || []).map((r) => ({
        ...r,
        due_date: r.due_date || computeDueDate(r.fund_type, r.wage_month),
      }));
      let mergedRecords = [];
      setRecords((prev) => {
        const seen = new Set(prev.map((p) => `${p.fund_type}|${p.wage_month}|${p.trrn_or_challan_no || ""}`));
        const fresh = enriched.filter((r) => {
          const k = `${r.fund_type}|${r.wage_month}|${r.trrn_or_challan_no || ""}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        mergedRecords = [...prev, ...fresh];
        return mergedRecords;
      });
      setParseErrors(data.errors || []);
      setFiles([]);
      if (enriched.length === 0) {
        toast.error("No records could be read. Please check the challan files and try again.");
      } else {
        toast.success(`${enriched.length} record(s) read from ${data.total_files} file(s).`);
        // Auto-scroll to the preview / totals area
        setTimeout(() => {
          document.getElementById("clause20b-preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === "AbortError") {
        toast.error("The request took too long — try uploading fewer files at once (5-6 max).");
      } else {
        toast.error(`Failed: ${e.message}`);
      }
    } finally {
      setIsParsing(false);
    }
  };

  const updateRecord = (idx, patch) => {
    setRecords((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      // Auto-recompute due_date & delay
      if (patch.wage_month !== undefined || patch.fund_type !== undefined) {
        next.due_date = computeDueDate(next.fund_type, next.wage_month);
      }
      const delay = computeDelay(next.due_date, next.payment_date);
      next.delay_days = delay;
      next.is_disallowed = delay != null && delay > 0;
      const empAmt = num(next.employees_contribution);
      next.allowable_amount = next.is_disallowed ? 0 : empAmt;
      next.disallowable_amount = next.is_disallowed ? empAmt : 0;
      return next;
    }));
  };

  const removeRecord = (idx) => setRecords((rs) => rs.filter((_, i) => i !== idx));

  const addBlankRow = () => {
    setRecords((rs) => [...rs, {
      fund_type: "PF",
      wage_month: "",
      employees_contribution: 0,
      payment_date: "",
      trrn_or_challan_no: "",
      due_date: "",
      delay_days: null,
      is_disallowed: false,
      allowable_amount: 0,
      disallowable_amount: 0,
    }]);
  };

  const handleExport = async () => {
    if (!records.length) { toast.error("No records to export."); return; }
    setIsExporting(true);
    try {
      const res = await fetch(`${API}/api/tax-audit/clause-20b/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ records, meta }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Clause_20b_Annexure_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Annexure downloaded.");
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-cyan-400 font-bold">Tax Audit · Form 3CD</div>
            <h1 className="text-lg sm:text-xl font-bold">Clause 20(b) — PF / ESI Annexure Builder</h1>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Beta · Free
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Intro */}
        <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-slate-300 leading-relaxed">
              Upload your client's <b>PF (ECR) and ESI challans</b> for the year. We parse each challan with AI,
              compute due dates & delays, flag disallowances u/s 36(1)(va) per the{" "}
              <em>Checkmate Services 2022 SC ruling</em>, and give you a 3CD-ready Excel annexure.
              Edit any row before you download — nothing is stored client-wise.
            </div>
          </div>
        </div>

        {/* Client meta */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5">
          <h2 className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">Client details (optional — shown on the Excel header)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Client Name"
              value={meta.client_name}
              onChange={(e) => setMeta({ ...meta, client_name: e.target.value })}
              className="bg-slate-950/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              data-testid="meta-client-name"
            />
            <input
              type="text"
              placeholder="PAN"
              value={meta.pan}
              onChange={(e) => setMeta({ ...meta, pan: e.target.value.toUpperCase() })}
              className="bg-slate-950/70 border border-slate-700 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:border-cyan-500"
              maxLength={10}
              data-testid="meta-pan"
            />
            <input
              type="text"
              placeholder="Assessment Year"
              value={meta.ay}
              onChange={(e) => setMeta({ ...meta, ay: e.target.value })}
              className="bg-slate-950/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
              data-testid="meta-ay"
            />
            <input
              type="text"
              placeholder="TAN"
              value={meta.tan}
              onChange={(e) => setMeta({ ...meta, tan: e.target.value.toUpperCase() })}
              className="bg-slate-950/70 border border-slate-700 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:border-cyan-500"
              maxLength={10}
              data-testid="meta-tan"
            />
          </div>
        </section>

        {/* Upload zone */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5">
          <h2 className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
            Step 1 — Upload PF / ESI challans (PDF · PNG · JPG)
          </h2>
          <label
            htmlFor="challan-upload"
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-700 hover:border-cyan-500/60 rounded-xl px-4 py-8 cursor-pointer transition-colors"
            data-testid="upload-dropzone"
          >
            <Upload className="w-8 h-8 text-cyan-400" />
            <div className="text-sm font-medium text-white">Click to select challans (multiple allowed)</div>
            <div className="text-xs text-slate-400">Max 30 files · 8 MB each</div>
            <input
              id="challan-upload"
              type="file"
              className="hidden"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={onFileChange}
              data-testid="upload-input"
            />
          </label>

          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2">
                  <FileSpreadsheet className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{f.name}</div>
                    <div className="text-[11px] text-slate-500">{(f.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    onClick={() => removeFile(i)}
                    className="p-1 text-slate-400 hover:text-red-400"
                    data-testid={`remove-file-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleSubmit}
                disabled={isParsing}
                className="mt-2 inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-full px-5 py-2.5 text-sm shadow-lg shadow-cyan-500/30 transition-all"
                data-testid="submit-btn"
              >
                {isParsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isParsing ? `Reading ${files.length} challan${files.length > 1 ? "s" : ""}…` : `Submit ${files.length} challan${files.length > 1 ? "s" : ""}`}
              </button>
            </div>
          )}

          {parseErrors.length > 0 && (
            <div className="mt-3 space-y-1">
              {parseErrors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div><b>{e.filename}</b>: {e.error}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Records preview */}
        {records.length > 0 && (
          <section id="clause20b-preview" className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.04] to-cyan-500/[0.04] p-4 sm:p-5 scroll-mt-24">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Step 2 · Your annexure is ready
                </div>
                <div className="text-sm text-slate-300 mt-0.5">Review the {records.length} row{records.length > 1 ? "s" : ""} below, edit anything if needed, then download the Excel.</div>
              </div>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-full px-5 py-2.5 text-sm shadow-lg shadow-emerald-500/30 transition-all"
                data-testid="export-btn"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isExporting ? "Building Excel…" : "Download Excel"}
              </button>
            </div>

            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Preview & edit rows</div>
              <button
                onClick={addBlankRow}
                className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                data-testid="add-row-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-[12.5px] min-w-[900px]">
                <thead>
                  <tr className="bg-slate-950/70 text-slate-400 text-[10.5px] uppercase tracking-wider">
                    <th className="px-2 py-2 text-left font-semibold">Fund</th>
                    <th className="px-2 py-2 text-left font-semibold">Wage Month</th>
                    <th className="px-2 py-2 text-right font-semibold">Employees'&nbsp;Contrib.</th>
                    <th className="px-2 py-2 text-left font-semibold">Due Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Payment Date</th>
                    <th className="px-2 py-2 text-center font-semibold">Delay</th>
                    <th className="px-2 py-2 text-left font-semibold">TRRN / Challan</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => {
                    const disallowed = r.is_disallowed;
                    return (
                      <tr
                        key={i}
                        className={`border-t border-slate-800 ${disallowed ? "bg-red-500/5" : ""}`}
                        data-testid={`record-row-${i}`}
                      >
                        <td className="px-2 py-2">
                          <select
                            value={r.fund_type || "PF"}
                            onChange={(e) => updateRecord(i, { fund_type: e.target.value })}
                            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[12px]"
                          >
                            <option value="PF">PF</option>
                            <option value="ESI">ESI</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="month"
                            value={r.wage_month || ""}
                            onChange={(e) => updateRecord(i, { wage_month: e.target.value })}
                            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[12px] w-[130px]"
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <input
                            type="number"
                            value={r.employees_contribution || 0}
                            onChange={(e) => updateRecord(i, { employees_contribution: Number(e.target.value) })}
                            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[12px] w-[110px] text-right"
                          />
                        </td>
                        <td className="px-2 py-2 text-slate-400">{r.due_date || "—"}</td>
                        <td className="px-2 py-2">
                          <input
                            type="date"
                            value={r.payment_date || ""}
                            onChange={(e) => updateRecord(i, { payment_date: e.target.value })}
                            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[12px] w-[135px]"
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          {r.delay_days == null ? (
                            <span className="text-slate-500">—</span>
                          ) : r.delay_days > 0 ? (
                            <span className="inline-flex items-center gap-1 text-red-400 font-semibold">
                              <AlertTriangle className="w-3 h-3" />+{r.delay_days}d
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-400">
                              <CheckCircle2 className="w-3 h-3" />on time
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={r.trrn_or_challan_no || ""}
                            onChange={(e) => updateRecord(i, { trrn_or_challan_no: e.target.value })}
                            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[12px] w-[150px]"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <button
                            onClick={() => removeRecord(i)}
                            className="p-1 text-slate-500 hover:text-red-400"
                            data-testid={`remove-row-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-700 bg-slate-950/60">
                    <td className="px-2 py-2 font-bold text-slate-400 text-[11px] uppercase tracking-wider" colSpan={2}>Total</td>
                    <td className="px-2 py-2 text-right font-bold">₹ {fmtINR(totals.emp)}</td>
                    <td colSpan={5}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold">Total employees' contribution</div>
                <div className="text-lg font-bold text-white mt-0.5">₹ {fmtINR(totals.emp)}</div>
              </div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-[10.5px] uppercase tracking-widest text-emerald-400 font-bold">Allowable</div>
                <div className="text-lg font-bold text-emerald-300 mt-0.5">₹ {fmtINR(totals.allow)}</div>
              </div>
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3">
                <div className="text-[10.5px] uppercase tracking-widest text-red-400 font-bold">Disallowable u/s 36(1)(va)</div>
                <div className="text-lg font-bold text-red-300 mt-0.5">₹ {fmtINR(totals.disallow)}</div>
              </div>
            </div>

            <button
              onClick={handleExport}
              disabled={isExporting}
              className="mt-5 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-full px-5 py-2.5 text-sm shadow-lg shadow-emerald-500/30 transition-all"
              data-testid="export-btn-bottom"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {isExporting ? "Building annexure…" : "Download 3CD Annexure (Excel)"}
            </button>
          </section>
        )}

        <footer className="pt-4 pb-8 text-[11px] text-slate-500 text-center">
          Powered by TaxSetu AI · Uses IT Act 2025 · Checkmate Services 2022 SC ruling applied strictly.
        </footer>
      </main>
    </div>
  );
}
